/**
 * Organization Context Service — Frontend API client for org context CRUD + tag creation
 */

import type { OrganizationContextData, OrganizationProfileData, BoostPolicyTag } from '../types/boostPolicy';
import { supabase } from '@foundation/utils/supabaseClient';
import { getActiveWorkspaceId } from '../../../header/activeWorkspace';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const getAuthHeaders = async (orgIdOverride?: string) => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const userId = session?.user?.id;

  const orgId = orgIdOverride ?? getActiveWorkspaceId() ?? '';

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-user-id': userId || '',
    'x-org-id': orgId,
  };
};

export const getOrgContext = async (orgId?: string): Promise<OrganizationContextData | null> => {
  const headers = await getAuthHeaders(orgId);
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-context`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to load organization context');
  }

  const data = await response.json();
  return data.organization_context || null;
};

export const saveOrgContext = async (context: OrganizationContextData, orgId?: string): Promise<void> => {
  const headers = await getAuthHeaders(orgId);
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-context`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ organization_context: context }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to save organization context');
  }
};

// Merge a profile into the org context without clobbering boost-policy fields.
// Reads existing context, splices in `profile`, and writes the merged object back.
export const saveOrgProfile = async (profile: OrganizationProfileData, orgId?: string): Promise<void> => {
  const existing = await getOrgContext(orgId);
  // Only persist organizationContext (free text), profile, and standard_answers.
  // industryTags / regionTags / workforceSize are boost-policy in-memory state only
  // and must not be written to base_tenants.organization_context.
  const merged: OrganizationContextData = {
    organizationContext: existing?.organizationContext,
    profile,
    standard_answers: existing?.standard_answers,
  };
  await saveOrgContext(merged, orgId);
};

// Save standard_answers from the org context accordion without clobbering profile or free-text.
export const saveStandardAnswers = async (
  answers: OrganizationContextData['standard_answers'],
  orgId?: string,
): Promise<void> => {
  const existing = await getOrgContext(orgId);
  const merged: OrganizationContextData = {
    ...existing,
    standard_answers: answers,
  };
  await saveOrgContext(merged, orgId);
};

export const fetchTagsByCategory = async (category: 'industry' | 'region'): Promise<BoostPolicyTag[]> => {
  const headers = await getAuthHeaders();
  const endpoint = category === 'industry' ? 'industries' : 'regions';
  const response = await fetch(`${API_BASE_URL}/api/revisr/onboarding/${endpoint}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${category} tags`);
  }

  return response.json();
};

export const createTagForBoost = async (name: string, category: 'industry' | 'region'): Promise<BoostPolicyTag> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/revisr/onboarding/tags`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ name, category }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create tag');
  }

  return response.json();
};

/**
 * Resolves the current user's active organisation ID from the WorkspaceSwitcher.
 */
export const getOrganizationId = async (): Promise<string | null> => {
  return getActiveWorkspaceId();
};

export const organizationContextService = {
  getOrgContext,
  saveOrgContext,
  saveOrgProfile,
  fetchTagsByCategory,
  createTagForBoost,
  getOrganizationId,
};
